import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatChipInputEvent } from '@angular/material/chips';
import { FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { emailFormatValidator } from '@app/utilities/device-alert';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { WebClient } from '@injectables';
import { DateTime } from '@lib/date-time';
import { Device } from '@models';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'off-spec-alerts',
    templateUrl: './off-spec-alerts.component.html',
    styleUrls: ['./off-spec-alerts.component.scss']
})
export class OffSpecAlertsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    public device: Device;
    public interval = new DateTime(new DateTime().setHours(0, 0, 0, 0));
    public alertEmails: string[] = [];
    public emailFormControl = new FormControl('', [emailFormatValidator]);
    public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    public readonly devices$ = this.fragQuery.devices$;
    public readonly device$ = this.fragQuery.device$;
    public readonly deviceUuid$ = this.fragQuery.deviceUuid$;
    public readonly particleSizeUnitSystem$ = this.fragQuery.particleSizeUnitSystem$;
    public offSpecAlertPercent = 0

    constructor(
        private readonly webClient: WebClient,
        private readonly fragQuery: FragQueryService,
        private readonly dialog: DialogService,
        private readonly snackBar: MatSnackBar
    ) { }

    ngOnInit(): void {
        this.device$
            .pipe(takeUntil(this.destroy$))
            .subscribe(device => this.updateDeviceSettings(device));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateDeviceSettings(device: Device): void {
        this.device = device;
        const deviceToOffSpec = device?.offSpecAlert ? JSON.parse(device.offSpecAlert) : null;

        this.interval = new DateTime(
            new DateTime().setHours(
                deviceToOffSpec?.duration?.hours ?? 0,
                deviceToOffSpec?.duration?.minutes ?? 0,
                0,
                0
            )
        );
        const emailString = device.alertEmail ?? '';
        if (emailString) {
            this.alertEmails = emailString.split(',').map((e) => e.trim()).filter((e) => e);
        } else {
            this.alertEmails = [];
        }
        this.offSpecAlertPercent = deviceToOffSpec?.offSpecAlertPercent
    }

    public onTimeIntervalChange(interval: DateTime): void {
        this.interval = interval;
    }

    public async saveSettings(): Promise<void> {
        if (!this.device) {
            this.snackBar.open('No device selected', 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success'],
            });
            return;
        }

        if (this.emailFormControl.invalid) {
            this.snackBar.open('Cannot save, invalid email format.', 'OK', {
                duration: 3000,
                panelClass: ['snackbar-success'],
            });
            return;
        }
        const offSpecAlert = {
            duration: {
                hours: this.interval.getHours(),
                minutes: this.interval.getMinutes()
            },
            offSpecAlertPercent: this.offSpecAlertPercent
        };

        this.device.offSpecAlert = JSON.stringify(offSpecAlert);
        this.device.alertEmail = this.alertEmails.join(',');
        await this.dialog.await(this.webClient.model.device.save(this.device));
        this.snackBar.open('Settings saved successfully', 'OK', {
            duration: 5000,
            panelClass: ['snackbar-success'],
        });
        this.fragQuery.trigger$.next();
    }

    //add email on the enter or (,) click
    public addEmail(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (!value) return;

        // Reset the form control before validating the new input
        this.emailFormControl.setValue(value);
        this.emailFormControl.markAsDirty();

        if (this.emailFormControl.valid && !this.alertEmails.includes(value) && value) {
            this.alertEmails.push(value);
            event.chipInput!.clear();
            this.emailFormControl.reset(); // Reset form control after adding valid email
        } else if (value) {
            this.snackBar.open('Invalid email format', 'OK', {
                duration: 3000, panelClass: ['snackbar-success'],
            });
        }
    }

    public removeEmail(email: string): void {
        const index = this.alertEmails.indexOf(email);

        if (index >= 0) {
            this.alertEmails.splice(index, 1);
        }
    }

    public valid() {
        if (this.interval.getHours() === 0 && this.interval.getMinutes() === 0) return false;
        const pendingEmail = this.emailFormControl.value?.trim();
        if (pendingEmail) {
            if (!this.emailFormControl.valid) {
                return false;
            }
        } else if (this.alertEmails.length === 0) {
            return false;
        }

        return true;
    }
}
